import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Play, Pause, MessageCircle, Share2, ChevronUp, ChevronDown, Music, User, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import BeatVisualizer from "../components/BeatVisualizer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BeatFeedPage() {
  const [previews, setPreviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filter, setFilter] = useState("trending");
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const containerRef = useRef(null);

  const token = localStorage.getItem("token");

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/feed/?filter=${filter}`);
      let items = res.data;
      // If no previews, fallback to beats as feed items
      if (items.length === 0) {
        const beatsRes = await axios.get(`${API}/api/beats`);
        items = beatsRes.data.map(b => ({
          preview_id: b.beat_id,
          producer_name: b.producer_name || "Gas Station",
          producer_avatar: "",
          title: b.title,
          audio_url: b.preview_url || b.audio_url,
          cover_url: b.image_url,
          description: `${b.genre} - ${b.bpm} BPM - Key: ${b.key}`,
          tags: b.tags || [],
          beat_id: b.beat_id,
          likes: b.likes || 0,
          plays: b.play_count || 0,
          comments: 0,
          basic_price: b.basic_price,
          created_at: b.created_at
        }));
      }
      setPreviews(items);
    } catch {
      toast.error("Failed to load feed");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
    }
    if (previews[currentIndex]?.audio_url && audioRef.current) {
      audioRef.current.src = previews[currentIndex].audio_url;
      audioRef.current.load();
    }
  }, [currentIndex, previews]);

  const togglePlay = () => {
    if (!audioRef.current || !previews[currentIndex]?.audio_url) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
      // Record play
      const p = previews[currentIndex];
      if (p.preview_id && !p.preview_id.startsWith("beat_")) {
        axios.post(`${API}/api/feed/preview/${p.preview_id}/play`).catch(() => {});
      }
    }
    setPlaying(!playing);
  };

  const goNext = useCallback(() => {
    if (currentIndex < previews.length - 1) setCurrentIndex(i => i + 1);
  }, [currentIndex, previews.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
  }, [currentIndex]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "j") goNext();
      if (e.key === "ArrowUp" || e.key === "k") goPrev();
      if (e.key === " ") { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev, togglePlay]);

  // Touch/scroll navigation
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    if (!touchStart.current) return;
    const diff = touchStart.current - e.changedTouches[0].clientY;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    touchStart.current = null;
  };

  const handleWheel = useCallback((e) => {
    if (e.deltaY > 30) goNext();
    else if (e.deltaY < -30) goPrev();
  }, [goNext, goPrev]);

  const current = previews[currentIndex];

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center pt-20">
        <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
      </div>
    );
  }

  if (previews.length === 0) {
    return (
      <div className="min-h-screen bg-dark flex flex-col items-center justify-center pt-20 gap-4">
        <Music className="w-16 h-16 text-zinc-600" />
        <h2 className="text-2xl text-zinc-400 font-light">No beats in the feed yet</h2>
        <p className="text-zinc-500">Check back later for fresh previews</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-black pt-16 overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      data-testid="beat-feed-page"
    >
      <audio ref={audioRef} onEnded={() => setPlaying(false)} />

      {/* Filter tabs */}
      <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 flex gap-2" data-testid="feed-filters">
        {["trending", "latest", "popular"].map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setCurrentIndex(0); }}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider transition-all ${
              filter === f
                ? "bg-neon-cyan text-black font-bold"
                : "bg-white/10 text-zinc-400 hover:bg-white/20"
            }`}
            data-testid={`feed-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Main card area */}
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-md mx-auto px-4"
          >
            {/* Card */}
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-900/90 backdrop-blur-xl shadow-2xl cursor-pointer"
              onClick={togglePlay}
              data-testid="feed-card"
            >
              {/* Cover image */}
              <div className="relative aspect-square">
                {current.cover_url || current.image_url ? (
                  <img
                    src={current.cover_url || current.image_url}
                    alt={current.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center">
                    <Music className="w-24 h-24 text-zinc-600" />
                  </div>
                )}
                {/* Beat Visualizer overlay */}
                <BeatVisualizer audioRef={audioRef} playing={playing} />
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity">
                  <motion.div
                    animate={playing ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      playing ? "bg-neon-cyan/80" : "bg-black/60 border-2 border-white/40"
                    }`}
                  >
                    {playing ? (
                      <Pause className="w-7 h-7 text-black" />
                    ) : (
                      <Play className="w-7 h-7 text-white ml-1" />
                    )}
                  </motion.div>
                </div>
                {/* Badge */}
                {current.basic_price && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-neon-cyan text-black font-bold text-xs">
                      From ${current.basic_price}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Info section */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neon-cyan to-neon-pink flex items-center justify-center overflow-hidden">
                    {current.producer_avatar ? (
                      <img src={current.producer_avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="text-zinc-300 text-sm font-medium">
                    {current.producer_name || "Unknown Producer"}
                  </span>
                </div>
                <h3 className="text-white text-lg font-bold mb-1" data-testid="feed-beat-title">
                  {current.title}
                </h3>
                {current.description && (
                  <p className="text-zinc-400 text-sm mb-2">{current.description}</p>
                )}
                {current.tags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {current.tags.slice(0, 4).map(tag => (
                      <span key={tag} className="text-xs text-neon-cyan/70 bg-neon-cyan/10 px-2 py-0.5 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Side action buttons */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-4 z-30">
          <FeedAction
            icon={Heart}
            count={current.likes || 0}
            previewId={current.preview_id}
            token={token}
          />
          <div className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white">
              <Play className="w-5 h-5" />
            </div>
            <span className="text-zinc-400 text-xs">{current.plays || 0}</span>
          </div>
          <FeedCommentAction
            previewId={current.preview_id}
            count={current.comments || 0}
            token={token}
          />
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success("Link copied!");
            }}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition"
            data-testid="feed-share-btn"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation arrows */}
        <div className="absolute left-6 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-30">
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 disabled:opacity-30 transition"
            data-testid="feed-prev-btn"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            disabled={currentIndex >= previews.length - 1}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 disabled:opacity-30 transition"
            data-testid="feed-next-btn"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        {/* Progress indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1 z-30">
          {previews.slice(Math.max(0, currentIndex - 3), currentIndex + 4).map((_, i) => {
            const actualIdx = Math.max(0, currentIndex - 3) + i;
            return (
              <div
                key={actualIdx}
                className={`rounded-full transition-all ${
                  actualIdx === currentIndex
                    ? "w-6 h-2 bg-neon-cyan"
                    : "w-2 h-2 bg-white/30"
                }`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FeedAction({ icon: Icon, count, previewId, token }) {
  const [liked, setLiked] = useState(false);
  const [localCount, setLocalCount] = useState(count);

  useEffect(() => { setLocalCount(count); setLiked(false); }, [count, previewId]);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!token) { toast.error("Sign in to like"); return; }
    if (!previewId || previewId.startsWith("beat_")) {
      // For fallback beats, use universal like
      try {
        const res = await axios.post(`${API}/api/like/beat/${previewId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLiked(res.data.action === "liked");
        setLocalCount(c => res.data.action === "liked" ? c + 1 : Math.max(0, c - 1));
      } catch { toast.error("Failed to like"); }
      return;
    }
    try {
      const res = await axios.post(`${API}/api/feed/preview/${previewId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiked(res.data.liked);
      setLocalCount(c => res.data.liked ? c + 1 : Math.max(0, c - 1));
    } catch { toast.error("Failed to like"); }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={handleLike}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
          liked ? "bg-red-500/80 text-white" : "bg-white/10 text-white hover:bg-white/20"
        }`}
        data-testid="feed-like-btn"
      >
        <Icon className="w-5 h-5" fill={liked ? "currentColor" : "none"} />
      </button>
      <span className="text-zinc-400 text-xs">{localCount}</span>
    </div>
  );
}

function FeedCommentAction({ previewId, count, token }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [localCount, setLocalCount] = useState(count);

  useEffect(() => { setLocalCount(count); }, [count]);

  const loadComments = async () => {
    if (!previewId || previewId.startsWith("beat_")) return;
    try {
      const res = await axios.get(`${API}/api/feed/preview/${previewId}/comments`);
      setComments(res.data);
    } catch {}
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpen(!open);
    if (!open) loadComments();
  };

  const submitComment = async () => {
    if (!text.trim() || !token) return;
    if (previewId.startsWith("beat_")) { toast("Comments coming soon!"); return; }
    try {
      await axios.post(`${API}/api/feed/preview/${previewId}/comment`,
        { text: text.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setText("");
      setLocalCount(c => c + 1);
      loadComments();
    } catch { toast.error("Failed to comment"); }
  };

  return (
    <div className="relative flex flex-col items-center gap-1">
      <button
        onClick={handleOpen}
        className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition"
        data-testid="feed-comment-btn"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
      <span className="text-zinc-400 text-xs">{localCount}</span>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute right-14 top-0 w-72 bg-zinc-900 border border-white/10 rounded-xl p-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-48 overflow-y-auto mb-2 space-y-2">
              {comments.length === 0 ? (
                <p className="text-zinc-500 text-xs text-center py-4">No comments yet</p>
              ) : comments.map(c => (
                <div key={c.comment_id} className="text-xs">
                  <span className="text-neon-cyan font-medium">{c.user_name}</span>
                  <span className="text-zinc-400 ml-2">{c.text}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Add comment..."
                className="text-xs h-8 bg-zinc-800 border-white/10"
                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                data-testid="feed-comment-input"
              />
              <Button
                size="sm"
                onClick={submitComment}
                className="h-8 px-3 bg-neon-cyan text-black text-xs"
                data-testid="feed-comment-submit"
              >
                Post
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
